<!-- src/views/legal/Security.vue -->
<!-- TEMPLATE — have counsel/security review before production reliance. -->

<script setup lang="ts">
  import { RouterLink } from 'vue-router';

  // Fixed publication date. Do NOT compute at runtime (no Date.now) so the
  // "Last updated" string is stable and auditable across renders/deploys.
  const lastUpdated = 'July 6, 2026';

  const h2 = 'mt-12 mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100';
  const h3 = 'mt-6 mb-2 text-base font-semibold text-gray-800 dark:text-gray-200';
  const p = 'mb-4 text-base leading-7 text-gray-600 dark:text-gray-300';
  const ul = 'mb-4 ml-5 list-disc space-y-2 text-base leading-7 text-gray-600 dark:text-gray-300';
  const a =
    'font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300';
</script>

<template>
  <div class="container mx-auto max-w-3xl px-4 py-8">
    <article aria-labelledby="security-title">
      <header>
        <h1
          id="security-title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          Security
        </h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
      </header>

      <p :class="[p, 'mt-8']">
        Security is the product. BurnLink is built so that the sensitive data you share is
        protected by design — encrypted at rest, revealed at most once, and destroyed the moment it
        is read. This page describes how the system is architected and how to report a
        vulnerability.
      </p>

      <h2 :class="h2">How your secrets are protected</h2>

      <h3 :class="h3">Encryption at rest</h3>
      <p :class="p">
        Secret content is encrypted with authenticated encryption before it is stored. New secret
        ciphertext uses XChaCha20-Poly1305 through libsodium, while older AES-256-GCM envelopes
        remain readable because the algorithm is recorded per envelope. We persist ciphertext, not
        plaintext. The system is deliberately designed so that operators cannot read the contents
        of a stored secret in the ordinary course of running the service.
      </p>

      <h3 :class="h3">Exactly-once reveal and atomic delete</h3>
      <p :class="p">
        A secret is intended to be revealed at most once. When a secret is viewed, the reveal and
        the deletion happen together: the record is consumed atomically, so a secret cannot be read
        twice, and a burned or expired secret leaves no recoverable copy behind. Nothing about the
        secret's content survives past its time-to-live.
      </p>
      <p :class="p">
        Automated link previews, security scanners, or whoever opens the link first can consume it
        before your intended recipient does — share one-time links thoughtfully.
      </p>

      <h3 :class="h3">Passphrase protection</h3>
      <p :class="p">
        Secrets can be protected with an optional passphrase. Passphrases are never stored in the
        clear — they are hashed with
        <strong class="font-semibold text-gray-800 dark:text-gray-200">argon2id</strong>, a modern,
        memory-hard password-hashing algorithm, and verified against that hash. We cannot recover a
        lost passphrase.
      </p>

      <h3 :class="h3">No plaintext in logs</h3>
      <p :class="p">
        Secret content and passphrases are kept out of application logs, error traces, and
        analytics. Operational logging is minimized and focused on reliability and abuse prevention
        rather than the data you share.
      </p>

      <h3 :class="h3">Transport and edge</h3>
      <p :class="p">
        Traffic is served over HTTPS/TLS. The application sits behind Cloudflare's edge network for
        TLS termination, CDN delivery, and DDoS/abuse mitigation, and runs on Fly.io for hosting.
        Security-related HTTP response headers (including a content security policy) are applied to
        harden the browser experience.
      </p>

      <h2 :class="h2">Responsible disclosure</h2>
      <p :class="p">
        We welcome reports from security researchers and treat them as a priority. If you believe
        you have found a vulnerability, please email
        <a :class="a" href="mailto:security@burnlink.app">security@burnlink.app</a> with enough
        detail to reproduce the issue, its potential impact, and your contact information.
      </p>
      <ul :class="ul">
        <li>Please give us a reasonable opportunity to investigate and remediate before any public disclosure.</li>
        <li>Do not access, modify, or destroy data that is not yours, and do not degrade the service for others.</li>
        <li>Avoid privacy violations, denial-of-service testing, and social engineering.</li>
        <li>Act in good faith, and we will act in good faith toward you.</li>
      </ul>
      <p :class="p">
        A machine-readable contact is published at
        <a :class="a" href="/.well-known/security.txt">/.well-known/security.txt</a>, and our full
        policy also lives in the repository's
        <a
          :class="a"
          href="https://github.com/onetimesecret/onetimesecret/blob/main/SECURITY.md"
          target="_blank"
          rel="noopener noreferrer">SECURITY.md</a>.
      </p>

      <h2 :class="h2">Related</h2>
      <p :class="p">
        See our <RouterLink :class="a" to="/privacy">Privacy Policy</RouterLink> for what we collect
        and retain, and our <RouterLink :class="a" to="/terms">Terms of Service</RouterLink> for the
        rules of use.
      </p>
    </article>
  </div>
</template>
